import { useDebounce } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { Line, getElementAtEvent } from 'react-chartjs-2'

import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useTheme } from '@apps-common/hooks/useTheme'
import { IUnitTrafficModel } from '@apps-common/types/portal/unit'
import { calcSignalStrength, hexToRgbA } from '@apps-common/utils/helpers'

import 'chart.js/auto'

interface IProps {
  loading?: boolean
  classes?: string
  chartClasses?: string
  hideGrid?: boolean
  data?: IUnitTrafficModel[]
  pointInteractionCallback?: (interactionItem?: IUnitTrafficModel) => void
}

export const SignalStrengthChart: React.FC<IProps> = ({ loading, classes, chartClasses, hideGrid, data, pointInteractionCallback }) => {
  const { resolvedTheme } = useTheme()
  const [showChart, setShowChart] = useState(false)
  const chartRef = useRef<any>(null)
  const [interactionItem, setInteractionItem] = useState<IUnitTrafficModel>()
  const debouncedParams = useDebounce(interactionItem, 500)

  const interactionCallback = (evt: any) => {
    const items = getElementAtEvent(chartRef.current, evt)

    if (items && items.length > 0) {
      const item = items[0]
      const dataset = [...(data || [])]?.reverse()

      if (item.index < dataset.length) {
        const datasetItem = dataset[item.index]
        if (datasetItem.id !== interactionItem?.id) {
          setInteractionItem(dataset[item.index])
        }
      }
    } else if (interactionItem) {
      setInteractionItem(undefined)
    }
  }

  useEffect(() => {
    if (!loading && !showChart) {
      const timer = window.setTimeout(() => {
        setShowChart(true)
      }, 200)

      return () => {
        window.clearTimeout(timer)
      }
    }
  }, [loading])

  useEffect(() => {
    pointInteractionCallback?.(debouncedParams)
  }, [debouncedParams])

  return (
    <div className={classNames(classes, 'min-h-[220px] relative')}>
      <AnimatePresence>
        {loading && !showChart && (
          <motion.div key='signal-strength-chart-skeleton' {...motionFadeIn(0.2)}>
            <div className='absolute top-0 sm:top-[4px] left-0 w-full h-full bg-skeleton rounded-[10px] z-[5] overflow-hidden' />
          </motion.div>
        )}
        {showChart && (
          <motion.div key='signal-strength-chart-container' {...motionFadeIn(0.2)}>
            <Line
              ref={chartRef}
              className={classNames(chartClasses, 'min-h-[220px] w-full relative')}
              onMouseEnter={interactionCallback}
              onMouseMove={interactionCallback}
              onMouseLeave={interactionCallback}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 2,
                clip: false,
                hover: {
                  mode: 'nearest',
                  intersect: true
                },
                layout: {
                  autoPadding: true
                },
                scales: {
                  y: {
                    min: 0,
                    max: 10,
                    border: {
                      display: false
                    },
                    ticks: {
                      color: hexToRgbA(resolvedTheme.textMuted, 0.9),
                      font: { family: 'jost-light' }
                    },
                    grid: {
                      display: !hideGrid,
                      color: hexToRgbA(resolvedTheme.border, 0.35)
                    }
                  },
                  x: {
                    border: { display: false },
                    ticks: { display: false },
                    grid: {
                      drawTicks: false,
                      drawOnChartArea: false
                    }
                  }
                },
                elements: {
                  line: {
                    tension: 0.2,
                    borderColor: resolvedTheme.brand,
                    borderJoinStyle: 'round'
                  },
                  point: {
                    borderColor: resolvedTheme.brand,
                    borderWidth: 2,
                    hitRadius: 10,
                    backgroundColor: resolvedTheme.surface,
                    pointStyle: 'circle'
                  }
                },
                plugins: {
                  tooltip: {
                    padding: 8,
                    cornerRadius: 8,
                    titleFont: { family: 'jost' },
                    bodyFont: { family: 'jost', size: 16 },
                    displayColors: false,

                    // Tooltip styling (readable in both modes)
                    titleColor: resolvedTheme.bg,
                    bodyColor: resolvedTheme.bg,
                    backgroundColor: resolvedTheme.text,

                    callbacks: {
                      title: () => '',
                      label: (context) => {
                        const id = context.label
                        const signal = data?.find((s) => s.id === parseInt(String(id), 10))
                        return `${calcSignalStrength(parseInt(signal?.signalStrength || '', 10))}`
                      }
                    }
                  },
                  legend: { display: false, position: 'top' as const },
                  title: { display: false, text: 'Signal Strength Chart' }
                }
              }}
              data={{
                labels: data?.map((s) => s.id).reverse() || [],
                datasets: [
                  {
                    label: 'Device Signals',
                    data: data?.map((s) => calcSignalStrength(parseInt(s.signalStrength, 10))).reverse() || []
                  }
                ]
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
